import React, { useState, useEffect, useMemo } from 'react';
import { X, Clock, ChevronDown, ChevronRight, GripVertical, Tag } from 'lucide-react';
import ShareDialog from './ShareDialog';
import TourOverlay from './TourOverlay';
import { useT } from './i18n/index';
import type { Box, Department, Resource, HistoryEntry } from './types';
import supabase from './supabase';

// ── Helpers ───────────────────────────────────────────────────────────────────

function getISOWeek(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function MiniCalendar({ datePart, month, onMonthChange, onDateSelect, accent = 'blue' }: {
  datePart: string;
  month: Date;
  onMonthChange: (d: Date) => void;
  onDateSelect: (iso: string) => void;
  accent?: 'blue' | 'orange';
}) {
  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(year, m, 1).getDay();
  const daysInMonth = new Date(year, m + 1, 0).getDate();
  const startOffset = (firstDay + 6) % 7;
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
  const selCls = accent === 'orange' ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-blue-500 text-white hover:bg-blue-600';
  const hovCls = accent === 'orange' ? 'hover:bg-orange-100' : 'hover:bg-blue-100';
  return (
    <div className="border rounded p-2 bg-white shadow text-xs select-none">
      <div className="flex items-center justify-between mb-1">
        <button className="px-1 hover:bg-gray-100 rounded" onClick={() => onMonthChange(new Date(year, m - 1, 1))}>‹</button>
        <span className="font-semibold">{MONTH_NAMES[m]} {year}</span>
        <button className="px-1 hover:bg-gray-100 rounded" onClick={() => onMonthChange(new Date(year, m + 1, 1))}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(7, 1fr)', textAlign: 'center' }} className="mb-1">
        <div className="font-semibold text-gray-300 text-right pr-1">Wk</div>
        {['Mo','Tu','We','Th','Fr','Sa','Su'].map(d => <div key={d} className="font-semibold text-gray-400">{d}</div>)}
      </div>
      {Array.from({ length: totalCells / 7 }, (_, rowIdx) => (
        <div key={rowIdx} style={{ display: 'grid', gridTemplateColumns: 'auto repeat(7, 1fr)', textAlign: 'center' }} className="gap-y-1 mb-0.5">
          <div className="text-gray-300 text-right pr-1 flex items-center justify-end">
            {getISOWeek(new Date(year, m, rowIdx * 7 - startOffset + 1))}
          </div>
          {Array.from({ length: 7 }, (_, colIdx) => {
            const dayNum = rowIdx * 7 + colIdx - startOffset + 1;
            if (dayNum < 1 || dayNum > daysInMonth) return <div key={colIdx} />;
            const iso = `${year}-${String(m + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
            const selected = iso === datePart;
            return (
              <button key={colIdx}
                className={`rounded py-0.5 ${hovCls} ${selected ? selCls : ''}`}
                onClick={() => onDateSelect(iso)}
              >{dayNum}</button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── Copy-to-flowchart dialog ──────────────────────────────────────────────────

function CopyToFlowchartDialog({ boxCount, currentFlowchartId, userId, onCopy, onClose }: {
  boxCount: number;
  currentFlowchartId: string | null;
  userId: string | null;
  onCopy: (targetId: string) => Promise<{ success: boolean; error?: string }>;
  onClose: () => void;
}) {
  const [flowcharts, setFlowcharts] = useState<{ id: string; title: string }[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [copying, setCopying] = useState(false);
  const [done, setDone] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('flowcharts')
        .select('id, title, flowchart_access!left(role)')
        .eq('flowchart_access.user_id', userId)
        .is('deleted_at', null)
        .order('title', { ascending: true });
      const filtered = (data || []).filter((f: any) => {
        const role = f.flowchart_access?.[0]?.role;
        return ['owner', 'editor'].includes(role) && f.id !== currentFlowchartId;
      });
      setFlowcharts(filtered);
      setLoadingList(false);
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = flowcharts.filter(f =>
    (f.title || '').toLowerCase().includes(search.toLowerCase())
  );

  const selectedTitle = flowcharts.find(f => f.id === selectedId)?.title || 'Untitled';

  const handleCopy = async () => {
    if (!selectedId) return;
    setCopying(true);
    setCopyError(null);
    const result = await onCopy(selectedId);
    if (result.success) {
      setDone(true);
    } else {
      setCopyError(result.error || 'Copy failed. Check your access rights.');
    }
    setCopying(false);
  };

  const label = `${boxCount} box${boxCount !== 1 ? 'es' : ''}`;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl p-5 w-full" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <h3 className="text-base font-semibold mb-1">Copy to flowchart</h3>
        <p className="text-xs text-gray-500 mb-3">
          {done
            ? `${label} copied to "${selectedTitle}".`
            : `Select a flowchart to copy ${label} to.`}
        </p>

        {!done && (
          <>
            <input
              type="text"
              placeholder="Search flowcharts..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm mb-2"
              autoFocus
            />
            <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              {loadingList ? (
                <div className="p-4 text-center text-sm text-gray-400">Loading...</div>
              ) : visible.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-400">No other flowcharts found</div>
              ) : (
                visible.map(f => (
                  <button
                    key={f.id}
                    className="w-full text-left px-3 py-2 text-sm"
                    style={{
                      background: selectedId === f.id ? '#e6f4f5' : undefined,
                      color: selectedId === f.id ? '#16697A' : '#374151',
                      fontWeight: selectedId === f.id ? 600 : 400,
                    }}
                    onClick={() => setSelectedId(f.id)}
                  >
                    {f.title || 'Untitled'}
                  </button>
                ))
              )}
            </div>
            {copyError && <p className="text-xs text-red-500 mt-2">{copyError}</p>}
          </>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50" onClick={onClose}>
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <button
              className="px-3 py-1.5 text-sm text-white rounded hover:opacity-90"
              style={{ background: '#16697A', opacity: (!selectedId || copying) ? 0.4 : 1, cursor: (!selectedId || copying) ? 'not-allowed' : 'pointer' }}
              disabled={!selectedId || copying}
              onClick={handleCopy}
            >
              {copying ? 'Copying...' : 'Copy'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface CanvasModalsProps {
  // Legend / Category
  category: {
    showLegend: boolean;
    onCloseLegend: () => void;
    departments: Department[];
    onDepartmentsChange: (d: Department[]) => void;
  };

  // Resources
  resourcesPanel: {
    showResources: boolean;
    onCloseResources: () => void;
    resources: Resource[];
    onResourcesChange: (r: Resource[]) => void;
  };

  // History panel
  historyPanel: {
    showHistory: boolean;
    onCloseHistory: () => void;
    loadingHistory: boolean;
    historyVersions: HistoryEntry[];
  };
  title: string; // shared with shareDialog below

  // Share dialog
  shareDialog: {
    showShareDialog: boolean;
    onCloseShareDialog: () => void;
    currentId: string | null;
    user: { id: string; email?: string } | null;
  };

  // Last-write toast
  lastWriteToast: string | null;

  // One-off notice when a box's duration source (manual vs subitems) flips
  durationModeToast: string | null;

  // Help panel
  helpPanel: {
    showHelpPanel: boolean;
    onCloseHelpPanel: () => void;
    onStartTour: () => void;
  };

  // Tour overlay
  tourOverlay: {
    showHelp: boolean;
    onCloseTour: () => void;
  };

  // Export menu
  exportMenu: {
    showExportMenu: boolean;
    exportMenuY: number;
    onCloseExportMenu: () => void;
    onExportAs: (fmt: 'png' | 'svg' | 'xlsx') => void;
  };

  // Link panel
  linkPanel: {
    state: { boxId: number } | null;
    linkInputRef: React.RefObject<HTMLInputElement>;
    onSaveLink: (boxId: number, url: string) => void;
    onDeleteLink: (boxId: number) => void;
    onCloseLinkPanel: () => void;
  };
  // UID panel — same shape as linkPanel, kept separate rather than generalized since
  // the two fields' editing UI genuinely differs (URL gets an "open link" action and
  // normalization, UID doesn't) — see FlowchartBuilder.tsx's own note on this choice.
  uidPanel: {
    state: { boxId: number } | null;
    uidInputRef: React.RefObject<HTMLInputElement>;
    onSaveUid: (boxId: number, uid: string) => void;
    onDeleteUid: (boxId: number) => void;
    onCloseUidPanel: () => void;
  };
  boxes: Box[]; // shared across linkPanel/uidPanel/contextMenu/startConstraintModal/autoEndModal
  canEditFlowchart: boolean; // shared across linkPanel/contextMenu

  // Context menu
  contextMenu: {
    state: { x: number; y: number; type: 'box' | 'canvas'; boxId?: number } | null;
    onCloseContextMenu: () => void;
    selectedBoxes: number[];
    clipboardHasItems: boolean;
    onContextMenuOpenStartConstraint: (boxId: number) => void;
    onOpenSubitems: (boxId: number) => void;
    onOpenUid: (boxId: number) => void;
    onContextMenuToggle24_7: (boxIds: number[]) => void;
    onContextMenuToggle24_5: (boxIds: number[]) => void;
    onContextMenuToggleMilestone: (boxIds: number[]) => void;
    onContextMenuCopy: (boxIds: number[]) => void;
    onContextMenuDuplicate: (boxIds: number[]) => void;
    onContextMenuOpenResizeNote: (boxId: number) => void;
    onContextMenuTogglePrivate: (boxId: number) => void;
    onContextMenuModifyLink: (boxId: number) => void;
    onContextMenuDeleteLink: (boxId: number) => void;
    onContextMenuDelete: (boxIds: number[]) => void;
    onContextMenuAddBox: () => void;
    onContextMenuAddNote: () => void;
    onContextMenuPaste: () => void;
    onContextMenuSelectAll: () => void;
  };

  // Start constraint modal (controlled by FlowchartBuilder) — combines the old
  // separate "Set fixed date" and "Set earliest start" popups into one, with a
  // dropdown choosing which type of precondition this box's start uses.
  startConstraintModal: {
    state: { boxId: number; type: 'fixed' | 'earliest'; value: string } | null;
    onChange: (v: { boxId: number; type: 'fixed' | 'earliest'; value: string }) => void;
    onSave: (boxId: number, type: 'fixed' | 'earliest', value: string) => void;
    onCancel: () => void;
    durationUnit: string;
  };

  // Arrow context menu
  arrowContextMenu: {
    state: { arrowId: number; x: number; y: number } | null;
    onCloseArrowContextMenu: () => void;
    onDeleteArrow: (arrowId: number) => void;
  };

  // Resize note modal (controlled by FlowchartBuilder)
  resizeNoteModal: {
    state: { boxId: number; width: number; height: number } | null;
    onResizeNoteModalChange: (v: { boxId: number; width: number; height: number }) => void;
    onApplyResizeNote: (boxId: number, width: number, height: number) => void;
    onCancelResizeNote: () => void;
  };

  // Autosave banner
  autosaveBanner: {
    pendingAutosave: { timestamp: number } | null;
    onRestoreAutosave: () => void;
    onDismissAutosave: () => void;
  };

  // Copy to flowchart
  copyToFlowchartModal: {
    state: { boxIds: number[] } | null;
    onOpenCopyToFlowchart: (boxIds: number[]) => void;
    onCloseCopyToFlowchart: () => void;
    onCopyToFlowchart: (targetId: string) => Promise<{ success: boolean; error?: string }>;
  };

  // Auto end (set automatic duration)
  autoEndModal: {
    state: { boxId: number; value: string } | null;
    onOpenAutoEnd: (boxId: number) => void;
    onAutoEndChange: (v: { boxId: number; value: string }) => void;
    onSaveAutoEnd: (boxId: number, value: string) => void;
    onCancelAutoEnd: () => void;
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CanvasModals(props: CanvasModalsProps) {
  const {
    category: { showLegend, onCloseLegend, departments, onDepartmentsChange },
    resourcesPanel: { showResources, onCloseResources, resources, onResourcesChange },
    historyPanel: { showHistory, onCloseHistory, loadingHistory, historyVersions },
    title,
    shareDialog: { showShareDialog, onCloseShareDialog, currentId, user },
    lastWriteToast,
    durationModeToast,
    helpPanel: { showHelpPanel, onCloseHelpPanel, onStartTour },
    tourOverlay: { showHelp, onCloseTour },
    exportMenu: { showExportMenu, exportMenuY, onCloseExportMenu, onExportAs },
    linkPanel: { state: linkPanel, linkInputRef, onSaveLink, onDeleteLink, onCloseLinkPanel },
    uidPanel: { state: uidPanel, uidInputRef, onSaveUid, onDeleteUid, onCloseUidPanel },
    boxes, canEditFlowchart,
    contextMenu: {
      state: contextMenu, onCloseContextMenu, selectedBoxes, clipboardHasItems,
      onContextMenuOpenStartConstraint, onOpenSubitems, onOpenUid, onContextMenuToggle24_7, onContextMenuToggle24_5, onContextMenuToggleMilestone,
      onContextMenuCopy, onContextMenuDuplicate, onContextMenuOpenResizeNote, onContextMenuTogglePrivate,
      onContextMenuModifyLink, onContextMenuDeleteLink, onContextMenuDelete,
      onContextMenuAddBox, onContextMenuAddNote, onContextMenuPaste, onContextMenuSelectAll,
    },
    startConstraintModal: { state: startConstraintModal, onChange: onStartConstraintChange, onSave: onSaveStartConstraint, onCancel: onCancelStartConstraint, durationUnit },
    arrowContextMenu: { state: arrowContextMenu, onCloseArrowContextMenu, onDeleteArrow },
    resizeNoteModal: { state: resizeNoteModal, onResizeNoteModalChange, onApplyResizeNote, onCancelResizeNote },
    autosaveBanner: { pendingAutosave, onRestoreAutosave, onDismissAutosave },
    copyToFlowchartModal: { state: copyToFlowchartModal, onOpenCopyToFlowchart, onCloseCopyToFlowchart, onCopyToFlowchart },
    autoEndModal: { state: autoEndModal, onOpenAutoEnd, onAutoEndChange, onSaveAutoEnd, onCancelAutoEnd },
  } = props;

  const { t } = useT();

  // While a team field is focused, keep that one resource in its ORIGINAL group
  // instead of regrouping after every keystroke — otherwise, as soon as the
  // partially-typed text stops matching any existing team, the row (and its
  // focused input) gets moved into a brand-new group section, which unmounts
  // and remounts the input and drops focus/cursor position mid-word. Tracked by
  // id (falling back to name for legacy resources without one) rather than
  // array index, since a collaborative merge can reorder `resources` while
  // this field is being edited.
  const [editingTeamFor, setEditingTeamFor] = useState<{ key: string; originalTeam: string } | null>(null);

  // Group resources by team so the panel looks the same as the assign dropdown.
  const resourceGroups = useMemo(() => {
    const groups = new Map<string, Resource[]>();
    resources.forEach(r => {
      const rKey = r.id ?? r.name;
      const key = (editingTeamFor && editingTeamFor.key === rKey) ? editingTeamFor.originalTeam : (r.team || '');
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(r);
    });
    return Array.from(groups.entries());
  }, [resources, editingTeamFor]);
  // Box assignment, Gantt resource filtering, and "select specific resource times" all
  // identify a resource by its name string (not a stable id) — two resources sharing a
  // name (even across different teams) are indistinguishable everywhere else in the app,
  // so flag duplicates here rather than let them silently merge with confusing results.
  const duplicateResourceNames = useMemo(() => {
    const counts = new Map<string, number>();
    resources.forEach(r => { if (r.name) counts.set(r.name, (counts.get(r.name) || 0) + 1); });
    return new Set([...counts.entries()].filter(([, c]) => c > 1).map(([name]) => name));
  }, [resources]);
  // Collapsed team sections — local UI state only, not persisted.
  const [collapsedTeams, setCollapsedTeams] = useState<Set<string>>(new Set());
  const toggleTeamCollapsed = (team: string) => {
    setCollapsedTeams(prev => {
      const next = new Set(prev);
      if (next.has(team)) next.delete(team); else next.add(team);
      return next;
    });
  };

  // Drag-to-reorder resources within their team (grip handle only — the row itself
  // stays non-draggable so clicking its inputs/buttons works normally).
  const [draggedResourceIdx, setDraggedResourceIdx] = useState<number | null>(null);
  const [resourceDragOverIdx, setResourceDragOverIdx] = useState<number | null>(null);
  const moveResource = (fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return;
    if ((resources[fromIdx].team || '') !== (resources[toIdx].team || '')) return; // only within the same team
    const next = [...resources];
    const [item] = next.splice(fromIdx, 1);
    const insertAt = fromIdx < toIdx ? toIdx - 1 : toIdx;
    next.splice(insertAt, 0, item);
    onResourcesChange(next);
  };

  // Category-color picker for a resource ("square" between name and team fields).
  const [categoryPickerFor, setCategoryPickerFor] = useState<number | null>(null);
  const [categoryPickerPos, setCategoryPickerPos] = useState<{ left: number; top?: number; bottom?: number }>({ left: 0, top: 0 });
  const setResourceCategory = (idx: number, category: string | undefined) => {
    const next = [...resources];
    next[idx] = { ...next[idx], category };
    onResourcesChange(next);
    setCategoryPickerFor(null);
  };

  // datePickerMonth is only ever used by the two date modals — keep it local
  const [datePickerMonth, setDatePickerMonth] = useState<Date>(new Date());

  // Reset calendar when the start constraint modal opens
  useEffect(() => {
    if (startConstraintModal) {
      const existing = startConstraintModal.value && !startConstraintModal.value.startsWith('W')
        ? new Date(startConstraintModal.value.split('T')[0]) : null;
      setDatePickerMonth(existing && !isNaN(existing.getTime()) ? existing : new Date());
    }
  }, [startConstraintModal?.boxId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (autoEndModal) {
      const existing = autoEndModal.value ? new Date(autoEndModal.value.split('T')[0]) : null;
      setDatePickerMonth(existing && !isNaN(existing.getTime()) ? existing : new Date());
    }
  }, [autoEndModal?.boxId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Legend overlay ───────────────────────────────────────────────────────────

  return (
    <>
      {showLegend && (
        <div className="fixed bg-white rounded-lg shadow-xl p-4 border-2 border-blue-500" style={{ position: 'fixed', bottom: '20px', right: '20px', left: 'auto', width: '320px', zIndex: 9999 }}>
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-sm">{t('categories')}</h3>
            <button onClick={onCloseLegend} className="text-gray-500 hover:text-gray-700"><X size={16} /></button>
          </div>
          <div className="space-y-2">
            {departments.map((dept, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (departments.length > 1) onDepartmentsChange(departments.filter((_, i) => i !== idx));
                  }}
                  className="text-red-500 hover:text-red-700"
                  title={t('deleteDepartment')}
                >
                  <X size={16} />
                </button>
                <div className="w-8 h-8 rounded border-2 border-gray-300" style={{ backgroundColor: dept.color }} />
                <input
                  value={dept.name}
                  onChange={(e) => {
                    const newDepts = [...departments];
                    newDepts[idx] = { ...newDepts[idx], name: e.target.value };
                    onDepartmentsChange(newDepts);
                  }}
                  className="flex-1 px-2 py-1 text-sm border rounded"
                />
                <input
                  type="color"
                  value={dept.color}
                  onChange={(e) => {
                    const newDepts = [...departments];
                    newDepts[idx] = { ...newDepts[idx], color: e.target.value };
                    onDepartmentsChange(newDepts);
                  }}
                  className="w-8 h-8 cursor-pointer"
                />
              </div>
            ))}
          </div>
          <button
            onClick={() => {
              const palette = ['#dcfce7','#fce7f3','#dbeafe','#fef9c3','#ffe4e6','#e0e7ff','#ccfbf1','#ffedd5','#f3e8ff','#d1fae5'];
              const nextColor = palette[departments.length % palette.length];
              onDepartmentsChange([...departments, { name: t('newCategory'), color: nextColor }]);
            }}
            className="mt-3 w-full bg-blue-500 text-white px-3 py-1 rounded text-sm"
          >
            {t('addCategory')}
          </button>
        </div>
      )}

      {showResources && (
        <div
          className="fixed bg-white rounded-lg shadow-xl border-2 border-blue-500"
          style={{ position: 'fixed', bottom: '20px', right: '20px', left: 'auto', width: '460px', maxHeight: 'calc(100vh - 40px)', zIndex: 9999, display: 'flex', flexDirection: 'column' }}
        >
          <div className="flex justify-between items-center p-4" style={{ flexShrink: 0, paddingBottom: resources.length > 0 ? 8 : 16 }}>
            <h3 className="font-bold text-sm">{t('resources')}</h3>
            <button onClick={onCloseResources} className="text-gray-500 hover:text-gray-700"><X size={16} /></button>
          </div>
          {resources.length === 0 ? (
            <p className="text-xs text-gray-500 px-4" style={{ paddingBottom: 16 }}>{t('noResourcesYet')}</p>
          ) : (
            <div className="px-4" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              {duplicateResourceNames.size > 0 && (
                <p className="text-xs" style={{ color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 8px', marginBottom: 10 }}>
                  {t('duplicateResourceNameWarning')}
                </p>
              )}
              <div className="space-y-3" style={{ paddingBottom: 12 }}>
                {resourceGroups.map(([team, group]) => {
                  const key = team || '__none__';
                  const isCollapsed = collapsedTeams.has(key);
                  return (
                    <div key={key}>
                      {resourceGroups.length > 1 && (
                        <button
                          onClick={() => toggleTeamCollapsed(key)}
                          className="flex items-center gap-1 text-xs font-bold text-gray-400 uppercase tracking-wide mb-1 hover:text-gray-600"
                          style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                          {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                          <span className="truncate">{team || t('noTeam')}</span>
                        </button>
                      )}
                      {!isCollapsed && (
                        <div className="space-y-1">
                          {group.map(r => {
                            const idx = resources.indexOf(r);
                            return (
                              <div
                                // r.id (set for every resource created after this field existed) keeps
                                // this row's identity stable across a collaborative merge reordering the
                                // array — with an index key, a mid-typing input could get silently
                                // reassigned to a different resource's value when the array shifted.
                                // Falls back to idx for legacy resources created before this field existed.
                                key={r.id ?? idx}
                                className="flex items-center gap-2"
                                onDragOver={(e) => {
                                  if (draggedResourceIdx === null || draggedResourceIdx === idx) return;
                                  if ((resources[draggedResourceIdx].team || '') !== (r.team || '')) return;
                                  e.preventDefault();
                                  setResourceDragOverIdx(idx);
                                }}
                                onDragLeave={() => setResourceDragOverIdx(prev => prev === idx ? null : prev)}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  if (draggedResourceIdx !== null) moveResource(draggedResourceIdx, idx);
                                  setDraggedResourceIdx(null);
                                  setResourceDragOverIdx(null);
                                }}
                                style={{
                                  opacity: draggedResourceIdx === idx ? 0.4 : 1,
                                  borderTop: resourceDragOverIdx === idx ? '2px solid #3b82f6' : '2px solid transparent',
                                }}
                              >
                                <span
                                  draggable
                                  onDragStart={(e) => { setDraggedResourceIdx(idx); e.dataTransfer.effectAllowed = 'move'; }}
                                  onDragEnd={() => { setDraggedResourceIdx(null); setResourceDragOverIdx(null); }}
                                  title={t('dragToReorderResource')}
                                  style={{ cursor: 'grab', color: '#9ca3af', flexShrink: 0, display: 'flex' }}
                                >
                                  <GripVertical size={14} />
                                </span>
                                <button
                                  onClick={() => onResourcesChange(resources.filter((_, i) => i !== idx))}
                                  className="text-red-500 hover:text-red-700"
                                  title={t('deleteResource')}
                                >
                                  <X size={16} />
                                </button>
                                <input
                                  value={r.name}
                                  placeholder={t('newResourceName')}
                                  onChange={(e) => {
                                    const newName = e.target.value;
                                    // Box assignment, Gantt filtering, and resource hours all identify a
                                    // resource by name — a new exact duplicate is rejected outright rather
                                    // than just flagged, so it can never actually be created going forward.
                                    // (Duplicates already saved from before this check still just get the
                                    // warning below, since silently renaming someone's existing data isn't ours to do.)
                                    if (newName && resources.some((other, i) => i !== idx && other.name === newName)) {
                                      alert(t('duplicateResourceNameBlocked'));
                                      return;
                                    }
                                    const next = [...resources];
                                    next[idx] = { ...next[idx], name: newName };
                                    onResourcesChange(next);
                                  }}
                                  title={duplicateResourceNames.has(r.name) ? t('duplicateResourceName') : undefined}
                                  className="flex-1 px-2 py-1 text-sm border rounded"
                                  style={duplicateResourceNames.has(r.name) ? { borderColor: '#ef4444', background: '#fef2f2' } : undefined}
                                />
                                <button
                                  onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const popupMaxHeight = 220; // matches the popup's own maxHeight below
                                    const spaceBelow = window.innerHeight - rect.bottom;
                                    // Not enough room below (resource is at the bottom of the list) but
                                    // enough above: open upward instead of letting it fall off the screen.
                                    if (spaceBelow < popupMaxHeight + 8 && rect.top > popupMaxHeight) {
                                      setCategoryPickerPos({ left: rect.left, bottom: window.innerHeight - rect.top + 4 });
                                    } else {
                                      setCategoryPickerPos({ left: rect.left, top: rect.bottom + 4 });
                                    }
                                    setCategoryPickerFor(categoryPickerFor === idx ? null : idx);
                                  }}
                                  title={t('linkCategory')}
                                  style={{
                                    width: 20, height: 20, borderRadius: 4, flexShrink: 0, cursor: 'pointer',
                                    border: '1px solid #d1d5db',
                                    background: departments.find(d => d.name === r.category)?.color || '#f3f4f6',
                                  }}
                                />
                                <input
                                  value={r.team || ''}
                                  onFocus={() => setEditingTeamFor({ key: r.id ?? r.name, originalTeam: r.team || '' })}
                                  onBlur={() => setEditingTeamFor(prev => (prev && prev.key === (r.id ?? r.name)) ? null : prev)}
                                  onChange={(e) => {
                                    const next = [...resources];
                                    next[idx] = { ...next[idx], team: e.target.value };
                                    onResourcesChange(next);
                                  }}
                                  list="resource-team-options"
                                  placeholder={t('team')}
                                  style={{ width: 130 }}
                                  className="px-2 py-1 text-sm border rounded"
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <datalist id="resource-team-options">
                {Array.from(new Set(resources.map(r => r.team).filter((t): t is string => !!t))).map(team => (
                  <option key={team} value={team} />
                ))}
              </datalist>
            </div>
          )}
          <div className="p-4" style={{ flexShrink: 0, paddingTop: 12, borderTop: resources.length > 0 ? '1px solid #f3f4f6' : 'none' }}>
            <button
              onClick={() => onResourcesChange([...resources, { id: crypto.randomUUID(), name: '', team: '' }])}
              className="w-full bg-blue-500 text-white px-3 py-1 rounded text-sm"
            >
              {t('addResource')}
            </button>
          </div>
        </div>
      )}

      {categoryPickerFor !== null && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10000 }} onClick={() => setCategoryPickerFor(null)} />
          <div style={{
            position: 'fixed', top: categoryPickerPos.top, bottom: categoryPickerPos.bottom, left: categoryPickerPos.left, zIndex: 10001,
            background: 'white', border: '1px solid #e5e7eb', borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: 6,
            display: 'flex', flexDirection: 'column', gap: 2, minWidth: 150, maxHeight: 220, overflowY: 'auto',
          }}>
            <button
              onClick={() => setResourceCategory(categoryPickerFor, undefined)}
              className="flex items-center gap-2 text-xs hover:bg-gray-100 rounded"
              style={{ background: 'none', border: 'none', padding: '6px 8px', cursor: 'pointer', textAlign: 'left', color: '#6b7280' }}
            >
              <span style={{ width: 14, height: 14, borderRadius: 3, border: '1px solid #d1d5db', background: '#f3f4f6', flexShrink: 0 }} />
              {t('noCategory')}
            </button>
            {departments.map((d, i) => (
              <button
                key={i}
                onClick={() => setResourceCategory(categoryPickerFor, d.name)}
                className="flex items-center gap-2 text-xs hover:bg-gray-100 rounded"
                style={{ background: 'none', border: 'none', padding: '6px 8px', cursor: 'pointer', textAlign: 'left', color: '#374151' }}
              >
                <span style={{ width: 14, height: 14, borderRadius: 3, background: d.color, flexShrink: 0 }} />
                <span className="truncate">{d.name}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Change Log Panel */}
      {showHistory && (
        <div style={{ position: 'fixed', top: 44, right: 0, width: 360, height: 'calc(100vh - 44px)', background: 'white', borderLeft: '1px solid #e5e7eb', boxShadow: '-4px 0 16px rgba(0,0,0,0.1)', zIndex: 300, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff7ed' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Clock size={15} color="#f97316" />
              <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>{t('changeLog')}</span>
              <span style={{ fontSize: 12, color: '#6b7280' }}>— {title}</span>
            </div>
            <button onClick={onCloseHistory} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2 }}>
              <X size={16} />
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
            {loadingHistory ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af' }}>{t('loading')}</div>
            ) : historyVersions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af' }}>
                <Clock size={36} style={{ margin: '0 auto 10px', display: 'block', opacity: 0.3 }} />
                <p style={{ fontSize: 13 }}>{t('noChangesLogged')}</p>
                <p style={{ fontSize: 11, marginTop: 4 }}>{t('changesTrackedAfterSave')}</p>
              </div>
            ) : historyVersions.map((entry, i) => (
              <div key={i} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #f3f4f6', marginBottom: 8, background: i === 0 ? '#fffbeb' : 'white' }}>
                <div style={{ fontSize: 13, color: '#1f2937', marginBottom: 3 }}>{entry.action}</div>
                <div style={{ fontSize: 11, color: '#9ca3af', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{entry.user_email || t('unknown')}</span>
                  <span>{new Date(entry.created_at).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Share Dialog */}
      {showShareDialog && currentId && user && (
        <ShareDialog
          flowchartId={currentId}
          flowchartTitle={title}
          user={user}
          onClose={onCloseShareDialog}
        />
      )}

      {/* Last-write toast */}
      {lastWriteToast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9997, background: 'rgba(17,24,39,0.92)', color: 'white', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          <span style={{ fontSize: 15 }}>✏️</span>
          <span>{t('updatedBy')} <strong>{lastWriteToast.includes('@') ? lastWriteToast.split('@')[0] : lastWriteToast}</strong></span>
        </div>
      )}

      {/* Duration-mode toast (manual <-> determined by subitems) */}
      {durationModeToast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9997, background: 'rgba(17,24,39,0.92)', color: 'white', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 500, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          <span style={{ fontSize: 15 }}>⏱️</span>
          <span>{durationModeToast}</span>
        </div>
      )}

      {/* Help panel */}
      {showHelpPanel && (
        <div style={{ position: 'fixed', top: 52, left: 68, zIndex: 400, width: 300, background: 'white', borderRadius: 10, boxShadow: '0 4px 24px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100dvh - 64px)' }}>
          <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{t('controlsReminder')}</span>
            <button onClick={onCloseHelpPanel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2, display: 'flex', alignItems: 'center', borderRadius: 4 }}><X size={14} /></button>
          </div>
          <div style={{ overflowY: 'auto', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              { icon: '🖱️', keys: t('ctrlScrollWheel'), action: t('ctrlZoom') },
              { icon: '🖱️', keys: t('ctrlRightDrag'), action: t('ctrlPan') },
              { icon: '🖱️', keys: t('ctrlLeftDrag'), action: t('ctrlSelectMultiple') },
              { icon: '🖱️', keys: t('ctrlDragCanvas'), action: t('ctrlDrawBox') },
              { icon: '⌨️', keys: 'Ctrl + C / V', action: t('ctrlCopyPaste') },
              { icon: '⌨️', keys: 'Ctrl + Z / Y', action: t('ctrlUndoRedo') },
              { icon: '⌨️', keys: 'Ctrl + Q', action: t('ctrlQuickConnect') },
              { icon: '⌨️', keys: t('ctrlDelete'), action: t('ctrlDeleteSelected') },
              { icon: '⌨️', keys: 'Ctrl + S', action: t('ctrlSave') },
            ].map(({ icon, keys, action }) => (
              <div key={keys} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, width: 18, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
                <span style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap', flexShrink: 0 }}>{keys}</span>
                <span style={{ fontSize: 11, color: '#6b7280' }}>{action}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '8px 14px 10px', borderTop: '1px solid #f3f4f6', flexShrink: 0 }}>
            <button
              onClick={() => { onCloseHelpPanel(); onStartTour(); }}
              style={{ width: '100%', padding: '6px 0', borderRadius: 6, border: 'none', background: 'linear-gradient(135deg, #6366f1, #3b82f6)', color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >{t('restartTour')}</button>
          </div>
        </div>
      )}

      {/* Tour overlay */}
      {showHelp && <TourOverlay onClose={onCloseTour} />}

      {/* Export menu */}
      {showExportMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 399 }} onClick={onCloseExportMenu} />
          <div style={{ position: 'fixed', left: 60, top: exportMenuY, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', zIndex: 400, padding: '6px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 100 }}>
            <button onClick={() => { onExportAs('png'); onCloseExportMenu(); }} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', textAlign: 'left' }}>PNG</button>
            <button onClick={() => { onExportAs('svg'); onCloseExportMenu(); }} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', textAlign: 'left' }}>SVG</button>
            <button onClick={() => { onExportAs('xlsx'); onCloseExportMenu(); }} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', textAlign: 'left' }} title={t('exportXlsxHint')}>XLSX</button>
          </div>
        </>
      )}

      {/* Link panel */}
      {linkPanel && (() => {
        const box = boxes.find(b => b.id === linkPanel.boxId);
        if (!box) return null;

        const normalizeUrl = (s: string) => {
          const t = s.trim();
          if (!t) return '';
          return /^https?:\/\//i.test(t) ? t : 'https://' + t;
        };
        const commitLink = () => {
          if (!linkInputRef.current) return;
          const url = normalizeUrl(linkInputRef.current.value);
          onSaveLink(linkPanel.boxId, url);
        };

        return (
          <div
            style={{ position: 'fixed', top: 52, right: 0, width: 360, background: 'white', borderLeft: '1px solid #e5e7eb', boxShadow: '-4px 0 16px rgba(0,0,0,0.1)', zIndex: 300, display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#eff6ff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>{t('link')}</span>
                <span style={{ fontSize: 12, color: '#6b7280', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>— {box?.text}</span>
              </div>
              <button onClick={() => { commitLink(); onCloseLinkPanel(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2 }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                ref={linkInputRef}
                type="url"
                placeholder="https://example.com"
                disabled={!canEditFlowchart}
                defaultValue={box.url ?? ''}
                onBlur={() => { if (canEditFlowchart && document.hasFocus()) commitLink(); }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); commitLink(); onCloseLinkPanel(); }
                  if (e.key === 'Escape') { onCloseLinkPanel(); }
                }}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 14, outline: 'none', boxSizing: 'border-box', color: '#1f2937', background: canEditFlowchart ? 'white' : '#f9fafb' }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
                <button
                  onClick={() => { const url = normalizeUrl(linkInputRef.current?.value ?? box.url ?? ''); if (url) window.open(url, '_blank', 'noopener,noreferrer'); }}
                  style={{ fontSize: 13, color: '#3b82f6', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontWeight: 600 }}
                >{t('openLink')}</button>
                {canEditFlowchart && box.url && (
                  <button
                    onClick={() => { onDeleteLink(linkPanel.boxId); onCloseLinkPanel(); }}
                    style={{ fontSize: 12, color: '#ef4444', background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '5px 10px', cursor: 'pointer' }}
                  >{t('deleteLink')}</button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* UID panel — same shape as the link panel above, kept as its own simpler
          block rather than generalized (see this file's own props-interface note). */}
      {uidPanel && (() => {
        const box = boxes.find(b => b.id === uidPanel.boxId);
        if (!box) return null;

        const commitUid = () => {
          if (!uidInputRef.current) return;
          onSaveUid(uidPanel.boxId, uidInputRef.current.value);
        };

        return (
          <div
            style={{ position: 'fixed', top: 52, right: 0, width: 320, background: 'white', borderLeft: '1px solid #e5e7eb', boxShadow: '-4px 0 16px rgba(0,0,0,0.1)', zIndex: 300, display: 'flex', flexDirection: 'column' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fffbeb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tag size={15} color="#d97706" />
                <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>{t('uidLabel')}</span>
                <span style={{ fontSize: 12, color: '#6b7280', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>— {box?.title}</span>
              </div>
              <button onClick={() => { commitUid(); onCloseUidPanel(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 2 }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                ref={uidInputRef}
                type="text"
                placeholder={t('uidLabel')}
                disabled={!canEditFlowchart}
                defaultValue={box.uid ?? ''}
                onBlur={() => { if (canEditFlowchart && document.hasFocus()) commitUid(); }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); commitUid(); onCloseUidPanel(); }
                  if (e.key === 'Escape') { onCloseUidPanel(); }
                }}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 14, outline: 'none', boxSizing: 'border-box', color: '#1f2937', background: canEditFlowchart ? 'white' : '#f9fafb' }}
              />
              {canEditFlowchart && box.uid && (
                <button
                  onClick={() => { onDeleteUid(uidPanel.boxId); onCloseUidPanel(); }}
                  style={{ alignSelf: 'flex-start', fontSize: 12, color: '#ef4444', background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '5px 10px', cursor: 'pointer' }}
                >{t('deleteUid')}</button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Context menu */}
      {contextMenu && (
        <div
          className="context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onMouseLeave={onCloseContextMenu}
        >
          {contextMenu.type === 'box' && (() => {
            const boxId = contextMenu.boxId ?? selectedBoxes[0];
            const box = boxes.find(b => b.id === boxId);
            const boxIds = selectedBoxes.length > 0 ? selectedBoxes : (boxId ? [boxId] : []);
            return (
              <>
                {canEditFlowchart && (
                  <>
                    <button className="context-menu-item" onClick={() => {
                      onContextMenuOpenStartConstraint(boxId!);
                      onCloseContextMenu();
                    }}>
                      {(() => {
                        if (box?.scheduledStart) return `📌 ${box.scheduledStart.split('T')[0]}`;
                        if (box?.earliestStart && !box.earliestStart.startsWith('W')) return `⏱ ${box.earliestStart.split('T')[0]}`;
                        if (box?.earliestStart) return `⏱ ${t('notBeforeWeek', { week: box.earliestStart.slice(1) })}`;
                        return t('setStartDate');
                      })()}
                    </button>
                    <button className="context-menu-item" onClick={() => {
                      onOpenAutoEnd(boxId!);
                      onCloseContextMenu();
                    }}>{t('setAutomaticDuration')}</button>
                    <div className="context-menu-divider" />
                    {box && box.type === 'regular' && (() => {
                      const allIs24_5 = boxIds.length > 0 && boxIds.every(id => boxes.find(b => b.id === id)?.is24_5);
                      const allIs24_7 = boxIds.length > 0 && boxIds.every(id => boxes.find(b => b.id === id)?.alwaysOn);
                      const allMilestone = boxIds.length > 0 && boxIds.every(id => boxes.find(b => b.id === id)?.importantFinish);
                      return (
                        <>
                          {boxIds.length === 1 && (
                            <button className="context-menu-item" onClick={() => {
                              onOpenSubitems(box.id);
                              onCloseContextMenu();
                            }}>{t('editSubitems')}</button>
                          )}
                          <button className="context-menu-item" onClick={() => {
                            onContextMenuToggle24_5(boxIds);
                            onCloseContextMenu();
                          }}>
                            <span style={{ marginRight: 6 }}>{allIs24_5 ? '✓' : ''}</span>{t('execution245')}
                          </button>
                          <button className="context-menu-item" onClick={() => {
                            onContextMenuToggle24_7(boxIds);
                            onCloseContextMenu();
                          }}>
                            <span style={{ marginRight: 6 }}>{allIs24_7 ? '✓' : ''}</span>{t('execution247')}
                          </button>
                          <button className="context-menu-item" onClick={() => {
                            onContextMenuToggleMilestone(boxIds);
                            onCloseContextMenu();
                          }}>
                            <span style={{ marginRight: 6 }}>{allMilestone ? '✓' : ''}</span>{t('makeMilestone')}
                          </button>
                        </>
                      );
                    })()}
                    <div className="context-menu-divider" />
                  </>
                )}
                <button className="context-menu-item" onClick={() => {
                  onContextMenuCopy(boxIds);
                  onCloseContextMenu();
                }}>{t('copyAction')}</button>
                {canEditFlowchart && (
                  <>
                    <button className="context-menu-item" onClick={() => {
                      onContextMenuDuplicate(boxIds);
                      onCloseContextMenu();
                    }}>{t('duplicate')}</button>
                    <button className="context-menu-item" onClick={() => {
                      onOpenCopyToFlowchart(boxIds);
                      onCloseContextMenu();
                    }}>Copy to flowchart...</button>
                    {box?.type === 'callout' && (
                      <>
                        <div className="context-menu-divider" />
                        <button className="context-menu-item" onClick={() => {
                          onContextMenuOpenResizeNote(box.id);
                          onCloseContextMenu();
                        }}>{t('resizeNote')}</button>
                        <button className="context-menu-item" onClick={() => {
                          onContextMenuTogglePrivate(box.id);
                          onCloseContextMenu();
                        }}>
                          {box.privateTo ? `🔓 ${t('removePrivateComment')}` : `🔒 ${t('makePrivateComment')}`}
                        </button>
                      </>
                    )}
                    <div className="context-menu-divider" />
                    {box && (
                      box.url ? (
                        <>
                          <button className="context-menu-item" onClick={() => { onContextMenuModifyLink(box.id); onCloseContextMenu(); }}>{t('modifyLink')}</button>
                          <button className="context-menu-item context-menu-item-danger" onClick={() => { onContextMenuDeleteLink(box.id); onCloseContextMenu(); }}>{t('deleteLink')}</button>
                        </>
                      ) : (
                        <button className="context-menu-item" onClick={() => { onContextMenuModifyLink(box.id); onCloseContextMenu(); }}>{t('addHyperlink')}</button>
                      )
                    )}
                    {box && boxIds.length === 1 && (
                      box.uid ? (
                        <>
                          <button className="context-menu-item" onClick={() => { onOpenUid(box.id); onCloseContextMenu(); }}>{t('editUid')}</button>
                          <button className="context-menu-item context-menu-item-danger" onClick={() => { onDeleteUid(box.id); onCloseContextMenu(); }}>{t('deleteUid')}</button>
                        </>
                      ) : (
                        <button className="context-menu-item" onClick={() => { onOpenUid(box.id); onCloseContextMenu(); }}>{t('addUid')}</button>
                      )
                    )}
                    <div className="context-menu-divider" />
                    <button className="context-menu-item context-menu-item-danger" onClick={() => {
                      onContextMenuDelete(boxIds);
                      onCloseContextMenu();
                    }}>{t('delete')}</button>
                  </>
                )}
              </>
            );
          })()}
          {contextMenu.type === 'canvas' && canEditFlowchart && (
            <>
              <button className="context-menu-item" onClick={() => { onContextMenuAddBox(); onCloseContextMenu(); }}>+ {t('box')}</button>
              <button className="context-menu-item" onClick={() => { onContextMenuAddNote(); onCloseContextMenu(); }}>+ {t('note')}</button>
              <div className="context-menu-divider" />
              <button
                className={`context-menu-item${!clipboardHasItems ? ' context-menu-item-disabled' : ''}`}
                onClick={() => { if (clipboardHasItems) { onContextMenuPaste(); } onCloseContextMenu(); }}
              >{t('paste')}</button>
              <button className="context-menu-item" onClick={() => { onContextMenuSelectAll(); onCloseContextMenu(); }}>{t('selectAll')}</button>
            </>
          )}
        </div>
      )}

      {/* Earliest start modal */}
      {/* Start constraint modal: combines "Set fixed date" and "Set earliest start" into
          one popup with a dropdown for which type of precondition applies. */}
      {startConstraintModal && (() => {
        const isFixed = startConstraintModal.type === 'fixed';
        const isWeeks = !isFixed && durationUnit === 'weeks';
        const box = boxes.find(b => b.id === startConstraintModal.boxId);
        const rawVal = startConstraintModal.value;
        const datePart = rawVal.includes('T') ? rawVal.split('T')[0] : rawVal;
        const timePart = rawVal.includes('T') ? rawVal.split('T')[1] : '';
        const accent = isFixed ? 'orange' : 'blue';

        const setDatePart = (d: string) => {
          const tp = startConstraintModal.value.includes('T') ? startConstraintModal.value.split('T')[1] : '';
          onStartConstraintChange({ ...startConstraintModal, value: tp ? `${d}T${tp}` : d });
        };
        const setTimePart = (tp: string) => {
          const d = startConstraintModal.value.includes('T') ? startConstraintModal.value.split('T')[0] : startConstraintModal.value;
          onStartConstraintChange({ ...startConstraintModal, value: d ? (tp ? `${d}T${tp}` : d) : startConstraintModal.value });
        };
        // Normalizes free-typed digits into a strict 24h "HH:MM" on blur — this
        // field used to be a native <input type="time">, whose AM/PM segment (shown
        // by some OS/browser locales) silently left the whole value incomplete/empty
        // when a user typed e.g. "08:00" without ever explicitly picking AM/PM,
        // which then round-tripped as a date with NO time at all and decoded to
        // midnight downstream — read by the user as "I typed 08:00 and it jumped to
        // 0:00". A plain text field with its own always-24h parser has no AM/PM
        // concept to leave unset in the first place.
        const normalizeTimeInput = (raw: string): string => {
          const digits = raw.replace(/\D/g, '');
          if (!digits) return '';
          let hh: number; let mm: number;
          if (raw.includes(':')) {
            const [h, m] = raw.split(':');
            hh = parseInt(h, 10) || 0; mm = parseInt(m, 10) || 0;
          } else if (digits.length <= 2) {
            hh = parseInt(digits, 10); mm = 0;
          } else if (digits.length === 3) {
            hh = parseInt(digits.slice(0, 1), 10); mm = parseInt(digits.slice(1), 10);
          } else {
            hh = parseInt(digits.slice(0, 2), 10); mm = parseInt(digits.slice(2, 4), 10);
          }
          hh = Math.min(23, Math.max(0, hh));
          mm = Math.min(59, Math.max(0, mm));
          return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
        };
        const setType = (type: 'fixed' | 'earliest') => {
          // Switching type shows whatever value this box already has for that type
          // (usually none, since the two are mutually exclusive), not the other type's value.
          const value = type === 'fixed' ? (box?.scheduledStart ?? '') : (box?.earliestStart ?? '');
          onStartConstraintChange({ ...startConstraintModal, type, value });
        };

        return (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onCancelStartConstraint}>
            <div className="bg-white rounded-lg shadow-xl p-5 w-full" style={{ maxWidth: 320 }} onClick={e => e.stopPropagation()}>
              <h3 className="text-base font-semibold mb-3">{t('setStartDateTitle')}</h3>

              <div className="flex flex-col gap-1 mb-3">
                <label className="text-xs font-medium text-gray-600">{t('startConstraintTypeLabel')}</label>
                <select
                  className="border rounded px-3 py-2 text-sm w-full"
                  value={startConstraintModal.type}
                  onChange={e => setType(e.target.value as 'fixed' | 'earliest')}
                >
                  <option value="fixed">{t('startConstraintTypeFixed')}</option>
                  <option value="earliest">{t('startConstraintTypeEarliest')}</option>
                </select>
              </div>

              <p className="text-xs text-gray-500 mb-3">
                {isFixed
                  ? t('setDateIntro', { box: box?.text ? `"${box.text}"` : t('thisTask') })
                  : t('earliestStartIntro', { box: box?.text ? `"${box.text}"` : t('thisBox') })}
              </p>

              {isWeeks ? (
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-medium text-gray-600">{t('weekNumber')}</label>
                  <input
                    type="number"
                    min={1} max={53}
                    className="border rounded px-3 py-2 text-sm w-full"
                    placeholder={t('weekPlaceholder')}
                    value={startConstraintModal.value.startsWith('W') ? startConstraintModal.value.slice(1) : ''}
                    onChange={e => onStartConstraintChange({ ...startConstraintModal, value: e.target.value ? `W${e.target.value}` : '' })}
                    autoFocus
                  />
                  {startConstraintModal.value && (
                    <p className="text-xs text-gray-500">{t('notBeforeWeek', { week: startConstraintModal.value.slice(1) })}</p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2 items-center">
                    <div className="flex flex-col gap-1 flex-1">
                      <label className="text-xs font-medium text-gray-600">{t('date')}</label>
                      <input
                        type="date"
                        className="border rounded px-3 py-2 text-sm flex-1"
                        value={datePart}
                        onChange={e => setDatePart(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="flex flex-col gap-1" style={{ minWidth: 90 }}>
                      <label className="text-xs font-medium text-gray-600">{t('time')} <span className="font-normal text-gray-400">{t('optional')}</span></label>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="08:00"
                        maxLength={5}
                        className="border rounded px-2 py-2 text-sm w-full"
                        // Previously defaulted this DISPLAY to '09:00' whenever no time was
                        // set, via `value={timePart || '09:00'}` — but that's purely a visual
                        // fallback, never actually committed unless the user happened to
                        // focus/interact with this specific field. Setting a date and saving
                        // without ever touching the time field looked like it had saved
                        // "09:00" (that's what was shown) while the actually-persisted
                        // scheduledStart had no time component at all — reported as a fixed
                        // start showing only a date with no time even though a specific time
                        // had clearly been chosen. Showing the field genuinely empty when
                        // unset means what's displayed always matches what's saved.
                        value={timePart}
                        // Permissive while typing — only normalize into a strict HH:MM on
                        // blur (see normalizeTimeInput's own comment), so partial input like
                        // "8" or "08:" isn't fought mid-keystroke.
                        onChange={e => { if (/^\d{0,2}:?\d{0,2}$/.test(e.target.value)) setTimePart(e.target.value); }}
                        onBlur={e => { if (e.target.value) setTimePart(normalizeTimeInput(e.target.value)); }}
                      />
                    </div>
                  </div>
                  <MiniCalendar
                    datePart={datePart}
                    month={datePickerMonth}
                    onMonthChange={setDatePickerMonth}
                    onDateSelect={setDatePart}
                    accent={accent}
                  />
                </div>
              )}

              <div className="flex justify-between mt-4 gap-2">
                {startConstraintModal.value && (
                  <button className="text-xs text-red-500 hover:underline" onClick={() => onSaveStartConstraint(startConstraintModal.boxId, startConstraintModal.type, '')}>{t('removeConstraint')}</button>
                )}
                <div className="flex gap-2 ml-auto">
                  <button className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50" onClick={onCancelStartConstraint}>{t('cancel')}</button>
                  <button
                    className="px-3 py-1.5 text-sm text-white rounded hover:opacity-90"
                    style={isFixed ? { background: '#d97706' } : { background: '#3b82f6' }}
                    onClick={() => onSaveStartConstraint(startConstraintModal.boxId, startConstraintModal.type, startConstraintModal.value)}
                  >{t('save')}</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Auto end modal (set automatic duration) */}
      {autoEndModal && (() => {
        const box = boxes.find(b => b.id === autoEndModal.boxId);
        const rawVal = autoEndModal.value;
        const datePart = rawVal.includes('T') ? rawVal.split('T')[0] : rawVal;
        const timePart = rawVal.includes('T') ? rawVal.split('T')[1] : '';

        const setDatePart = (d: string) => {
          const tp = autoEndModal.value.includes('T') ? autoEndModal.value.split('T')[1] : '';
          onAutoEndChange({ ...autoEndModal, value: tp ? `${d}T${tp}` : d });
        };
        const setTimePart = (tp: string) => {
          const d = autoEndModal.value.includes('T') ? autoEndModal.value.split('T')[0] : autoEndModal.value;
          onAutoEndChange({ ...autoEndModal, value: d ? (tp ? `${d}T${tp}` : d) : autoEndModal.value });
        };

        return (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onCancelAutoEnd}>
            <div className="bg-white rounded-lg shadow-xl p-5 w-full" style={{ maxWidth: 320 }} onClick={e => e.stopPropagation()}>
              <h3 className="text-base font-semibold mb-3">{t('setAutomaticDurationTitle')}</h3>
              <p className="text-xs text-gray-500 mb-3">
                {t('setAutomaticDurationIntro', { box: box?.text ? `"${box.text}"` : t('thisTask') })}
              </p>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 items-center">
                  <div className="flex flex-col gap-1 flex-1">
                    <label className="text-xs font-medium text-gray-600">End date</label>
                    <input
                      type="date"
                      className="border rounded px-3 py-2 text-sm flex-1"
                      value={datePart}
                      onChange={e => setDatePart(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="flex flex-col gap-1" style={{ minWidth: 90 }}>
                    <label className="text-xs font-medium text-gray-600">Time <span className="font-normal text-gray-400">(optional)</span></label>
                    <input
                      type="time"
                      className="border rounded px-2 py-2 text-sm w-full"
                      value={timePart || '17:00'}
                      onChange={e => setTimePart(e.target.value)}
                      onFocus={e => { if (!timePart) setTimePart(e.target.value); }}
                    />
                  </div>
                </div>
                <MiniCalendar
                  datePart={datePart}
                  month={datePickerMonth}
                  onMonthChange={setDatePickerMonth}
                  onDateSelect={setDatePart}
                  accent="blue"
                />
              </div>
              <div className="flex justify-end mt-4 gap-2">
                <button className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50" onClick={onCancelAutoEnd}>{t('cancel')}</button>
                <button className="px-3 py-1.5 text-sm text-white rounded hover:opacity-90" style={{ background: '#16697A' }} onClick={() => onSaveAutoEnd(autoEndModal.boxId, autoEndModal.value)}>{t('save')}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Arrow context menu (desktop right-click, or mobile long-press) */}
      {arrowContextMenu && (
        <div
          className="fixed z-50"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
          onTouchStart={onCloseArrowContextMenu}
          onClick={onCloseArrowContextMenu}
        >
          <div
            className="absolute bg-white rounded-lg shadow-xl border border-gray-200 py-1"
            style={{ top: arrowContextMenu.y, left: arrowContextMenu.x, minWidth: 160, transform: 'translate(-50%, 8px)' }}
            onClick={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
          >
            <button
              className="context-menu-item w-full text-left text-red-600"
              onClick={() => { onDeleteArrow(arrowContextMenu.arrowId); onCloseArrowContextMenu(); }}
            >
              {t('deleteConnection')}
            </button>
          </div>
        </div>
      )}

      {/* Resize note modal */}
      {resizeNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onCancelResizeNote}>
          <div className="bg-white rounded-lg shadow-xl p-5 w-full" style={{ maxWidth: 280 }} onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold mb-4">{t('resizeNoteTitle')}</h3>
            <div className="flex flex-col gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('widthPx')}</label>
                <input
                  type="number" min={120} max={800}
                  className="border rounded px-3 py-2 text-sm w-full"
                  value={resizeNoteModal.width}
                  onChange={e => onResizeNoteModalChange({ ...resizeNoteModal, width: Number(e.target.value) })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('heightPx')}</label>
                <input
                  type="number" min={80} max={600}
                  className="border rounded px-3 py-2 text-sm w-full"
                  value={resizeNoteModal.height}
                  onChange={e => onResizeNoteModalChange({ ...resizeNoteModal, height: Number(e.target.value) })}
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50" onClick={onCancelResizeNote}>{t('cancel')}</button>
              <button className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600" onClick={() => onApplyResizeNote(resizeNoteModal.boxId, resizeNoteModal.width, resizeNoteModal.height)}>{t('apply')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Copy to flowchart dialog */}
      {copyToFlowchartModal && (
        <CopyToFlowchartDialog
          boxCount={copyToFlowchartModal.boxIds.length}
          currentFlowchartId={currentId}
          userId={user?.id ?? null}
          onCopy={onCopyToFlowchart}
          onClose={onCloseCopyToFlowchart}
        />
      )}

      {/* Autosave banner */}
      {pendingAutosave && !currentId && (
        <div className="autosave-banner">
          <span>{t('unsavedWorkFound', { date: new Date(pendingAutosave.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) })}</span>
          <button onClick={onRestoreAutosave} className="autosave-banner-btn-restore">{t('restore')}</button>
          <button onClick={onDismissAutosave} className="autosave-banner-btn-dismiss">{t('discard')}</button>
        </div>
      )}
    </>
  );
}
